import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

/**
 * Converts a storage path or URL to a full public URL.
 * If the path already starts with http, use it directly.
 * Otherwise build: ${SUPABASE_URL}/storage/v1/object/public/shipment-photos/${path}
 */
const toPublicUrl = (pathOrUrl) => {
  if (!pathOrUrl) return null;
  if (pathOrUrl?.startsWith('http://') || pathOrUrl?.startsWith('https://')) {
    return pathOrUrl;
  }
  return `${SUPABASE_URL}/storage/v1/object/public/shipment-photos/${pathOrUrl}`;
};

/**
 * PackagePhotoSection — primary source: shipment.photos_urls + shipment.photo_url
 * Secondary fallback: files prop (shipment_files with file_type='photo')
 */
const PackagePhotoSection = ({ shipment, files }) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

  const photosUrls = useMemo(() => {
    const seen = new Set();
    const result = [];

    const addUrl = (url) => {
      const resolved = toPublicUrl(url);
      if (resolved && !seen?.has(resolved)) {
        seen?.add(resolved);
        result?.push(resolved);
      }
    };

    // PRIMARY: shipment.photos_urls (array)
    if (Array.isArray(shipment?.photos_urls)) {
      shipment?.photos_urls?.forEach(addUrl);
    }

    // FALLBACK 1: shipment.photo_url (single)
    if (shipment?.photo_url) {
      addUrl(shipment?.photo_url);
    }

    // FALLBACK 2: files from RPC (file_type='photo') — only if nothing found above
    if (result?.length === 0) {
      const photoFiles = (files || [])?.filter(f => f?.file_type === 'photo');
      photoFiles?.forEach(f => f?.file_url && addUrl(f?.file_url));
    }

    return result;
  }, [shipment?.photos_urls, shipment?.photo_url, files]);

  if (photosUrls?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Camera" size={20} className="text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Photos du colis</h3>
        </div>
        <div className="text-center py-10">
          <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-muted flex items-center justify-center">
            <img
              src="/assets/images/no_image.png"
              alt="Aucune photo disponible"
              className="w-16 h-16 object-contain opacity-50"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-muted-foreground"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
              }}
            />
          </div>
          <p className="text-base font-medium text-foreground mb-1">Aucune photo disponible</p>
          <p className="text-sm text-muted-foreground">Les photos du colis apparaîtront ici une fois ajoutées</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Camera" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Photos du colis</h3>
            <p className="text-sm text-muted-foreground">{photosUrls?.length} photo{photosUrls?.length > 1 ? 's' : ''} disponible{photosUrls?.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        
        {/* Responsive photo grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photosUrls?.map((photoUrl, index) => (
            <div 
              key={index}
              className="relative group cursor-pointer aspect-square overflow-hidden rounded-xl border border-border hover:border-primary transition-all duration-200 hover:shadow-md"
              onClick={() => setSelectedPhotoIndex(index)}
            >
              <img
                src={photoUrl}
                alt={`Photo du colis ${index + 1}`}
                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                loading="lazy"
                onError={(e) => {
                  e.target.src = '/assets/images/no_image.png';
                  e.target.alt = 'Image non disponible';
                }}
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-full p-2">
                  <Icon name="ZoomIn" size={20} className="text-white" />
                </div>
              </div>
              {/* Photo number badge */}
              <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded-md font-medium">
                {index + 1}/{photosUrls?.length}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Zoom Modal */}
      {selectedPhotoIndex !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhotoIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setSelectedPhotoIndex(null)}
            className="absolute top-4 right-4 p-2.5 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
            aria-label="Fermer"
          >
            <Icon name="X" size={22} />
          </button>

          {/* Previous button */}
          {selectedPhotoIndex > 0 && (
            <button
              onClick={(e) => { e?.stopPropagation(); setSelectedPhotoIndex(selectedPhotoIndex - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              aria-label="Photo précédente"
            >
              <Icon name="ChevronLeft" size={24} />
            </button>
          )}

          {/* Next button */}
          {selectedPhotoIndex < photosUrls?.length - 1 && (
            <button
              onClick={(e) => { e?.stopPropagation(); setSelectedPhotoIndex(selectedPhotoIndex + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
              aria-label="Photo suivante"
            >
              <Icon name="ChevronRight" size={24} />
            </button>
          )}

          {/* Main image */}
          <div className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e?.stopPropagation()}>
            <img
              src={photosUrls?.[selectedPhotoIndex]}
              alt={`Photo du colis ${selectedPhotoIndex + 1}`}
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
              loading="lazy"
              onError={(e) => {
                e.target.src = '/assets/images/no_image.png';
                e.target.alt = 'Image non disponible';
              }}
            />
            <div className="mt-4 text-center">
              <p className="text-white/80 text-sm font-medium">
                Photo {selectedPhotoIndex + 1} sur {photosUrls?.length}
              </p>
              {/* Dot indicators */}
              {photosUrls?.length > 1 && (
                <div className="flex justify-center space-x-1.5 mt-2">
                  {photosUrls?.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e?.stopPropagation(); setSelectedPhotoIndex(i); }}
                      className={`w-2 h-2 rounded-full transition-all ${i === selectedPhotoIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/60'}`}
                      aria-label={`Aller à la photo ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PackagePhotoSection;